(function () {
  const eligibilityCache = new Map();
  const apiDomains = {
    live: 'https://api.getalma.eu',
    test: 'https://api.sandbox.getalma.eu'
  };

  class AlmaCustomWidget extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedToAlma) {
        return;
      }

      this.isConnectedToAlma = true;
      this.requestSequence = 0;
      this.eventController = new AbortController();
      this.dialog = this.querySelector('[data-alma-dialog]');
      this.summary = this.querySelector('[data-alma-summary]');
      this.plansContainer = this.querySelector('[data-alma-plans]');
      this.scheduleContainer = this.querySelector('[data-alma-schedule]');
      this.openButtons = this.querySelectorAll('[data-alma-open]');
      this.closeButton = this.querySelector('[data-alma-close]');

      const { signal } = this.eventController;
      this.openButtons.forEach((button) => button.addEventListener('click', () => this.open(), { signal }));
      this.closeButton?.addEventListener('click', () => this.close(), { signal });
      this.dialog?.addEventListener('click', (event) => this.closeFromBackdrop(event), { signal });
      this.plansContainer?.addEventListener('click', (event) => this.selectPlanFromEvent(event), { signal });
      document.addEventListener('variant:change', (event) => this.onVariantChange(event), { signal });

      this.refreshEligibility();
    }

    disconnectedCallback() {
      this.eventController?.abort();
      this.requestController?.abort();
      this.isConnectedToAlma = false;
    }

    async refreshEligibility() {
      const merchantId = this.dataset.almaMerchantId?.trim();
      const purchaseAmount = Number(this.dataset.productPrice);
      const currencyCode = this.dataset.currencyCode?.toUpperCase();

      this.hidden = true;
      this.close();

      if (!merchantId || merchantId === 'merchant_' || !Number.isInteger(purchaseAmount) || purchaseAmount <= 0 || currencyCode !== 'EUR') {
        return;
      }

      const requestId = ++this.requestSequence;
      this.requestController?.abort();
      this.requestController = new AbortController();

      try {
        const plans = await this.fetchEligibility(merchantId, purchaseAmount, this.requestController.signal);

        if (requestId !== this.requestSequence) {
          return;
        }

        this.plans = plans.filter((plan) => {
          const hasSchedule = plan?.eligible && Array.isArray(plan.payment_plan) && plan.payment_plan.length > 0;
          return hasSchedule && (Number(plan.installments_count) > 1 || this.isDeferred(plan));
        });

        if (this.plans.length === 0) {
          return;
        }

        this.selectedPlanIndex = this.getPreferredPlanIndex();
        this.render();
        this.hidden = false;
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.hidden = true;
        }
      }
    }

    async fetchEligibility(merchantId, purchaseAmount, signal) {
      const mode = this.dataset.almaMode === 'test' ? 'test' : 'live';
      const endpoint = `${apiDomains[mode]}/v2/payments/eligibility`;
      const cacheKey = `${mode}:${merchantId}:${purchaseAmount}`;

      if (eligibilityCache.has(cacheKey)) {
        return eligibilityCache.get(cacheKey);
      }

      const request = fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Alma-Merchant-Auth ${merchantId}`,
          'Content-Type': 'application/json',
          'X-Alma-Agent': 'Or-Azur Custom Widget/1.0'
        },
        body: JSON.stringify({
          purchase_amount: purchaseAmount,
          origin: 'online'
        }),
        signal
      }).then(async (response) => {
        if (!response.ok) {
          throw new Error(`Alma eligibility request failed with status ${response.status}`);
        }

        const payload = await response.json();

        if (!Array.isArray(payload)) {
          throw new TypeError('Alma eligibility response must be an array');
        }

        return payload;
      });

      eligibilityCache.set(cacheKey, request);

      try {
        return await request;
      } catch (error) {
        eligibilityCache.delete(cacheKey);
        throw error;
      }
    }

    getPreferredPlanIndex() {
      const preferredInstallments = Number(this.dataset.preferredInstallments);
      const preferredIndex = this.plans.findIndex((plan) => Number(plan.installments_count) === preferredInstallments);

      if (preferredIndex >= 0) {
        return preferredIndex;
      }

      const installmentIndex = this.plans.findIndex((plan) => Number(plan.installments_count) > 1);
      return installmentIndex >= 0 ? installmentIndex : 0;
    }

    render() {
      this.renderPlanButtons();
      this.renderSelectedPlan();
    }

    renderPlanButtons() {
      this.plansContainer.replaceChildren();

      this.plans.forEach((plan, index) => {
        const button = document.createElement('button');
        const isSelected = index === this.selectedPlanIndex;

        button.type = 'button';
        button.className = 'alma-custom__plan-button';
        button.dataset.almaPlanIndex = String(index);
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-selected', String(isSelected));
        button.tabIndex = isSelected ? 0 : -1;
        button.textContent = this.getPlanName(plan);
        this.plansContainer.append(button);
      });
    }

    renderSelectedPlan() {
      const plan = this.plans[this.selectedPlanIndex];

      if (!plan) {
        return;
      }

      this.renderSummary(plan);
      this.renderSchedule(plan);
      this.renderFinancing(plan);

      this.plansContainer.querySelectorAll('[data-alma-plan-index]').forEach((button, index) => {
        const isSelected = index === this.selectedPlanIndex;
        button.setAttribute('aria-selected', String(isSelected));
        button.tabIndex = isSelected ? 0 : -1;
      });
    }

    renderSummary(plan) {
      const installmentsCount = Number(plan.installments_count);
      const paymentPlan = plan.payment_plan;
      const firstPayment = paymentPlan[0];

      if (installmentsCount > 1) {
        const firstAmount = Number(firstPayment.total_amount || 0);
        const paymentsAreEqual = paymentPlan.every(
          (installment) => Number(installment.total_amount) === firstAmount
        );
        const remainingPayments = paymentPlan.slice(1);
        const remainingAmount = Number(remainingPayments[0]?.total_amount || 0);
        const remainingPaymentsAreEqual = remainingPayments.every(
          (installment) => Number(installment.total_amount) === remainingAmount
        );

        if (this.getTotalCost(plan) === 0 && paymentsAreEqual) {
          this.summary.textContent = this.interpolate(this.dataset.summaryEqual, {
            count: installmentsCount,
            amount: this.formatMoney(firstAmount)
          });
        } else if (installmentsCount === 2 && remainingPaymentsAreEqual) {
          this.summary.textContent = this.interpolate(this.dataset.summaryTwoInstallments, {
            first: this.formatMoney(firstPayment.total_amount),
            remaining: this.formatMoney(remainingAmount)
          });
        } else if (remainingPaymentsAreEqual) {
          this.summary.textContent = this.interpolate(this.dataset.summaryInstallments, {
            first: this.formatMoney(firstPayment.total_amount),
            remaining_count: remainingPayments.length,
            remaining: this.formatMoney(remainingAmount)
          });
        } else {
          const amounts = paymentPlan.map((installment) => Number(installment.total_amount));
          this.summary.textContent = this.interpolate(this.dataset.summaryVariable, {
            count: installmentsCount,
            minimum: this.formatMoney(Math.min(...amounts)),
            maximum: this.formatMoney(Math.max(...amounts))
          });
        }
      } else if (this.isDeferred(plan)) {
        this.summary.textContent = this.interpolate(this.dataset.summaryDeferred, {
          date: this.formatDate(firstPayment.due_date)
        });
      } else {
        this.summary.textContent = this.getPlanName(plan);
      }
    }

    renderFinancing(plan) {
      // Alma's widget V4 converts annual_interest_rate from basis points.
      const rate = plan.annual_interest_rate;
      const hasRate = typeof rate === 'number' && Number.isFinite(rate) && rate >= 0;
      const formatRate = (value) => new Intl.NumberFormat(this.dataset.locale || 'fr-FR', {
        style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2
      }).format(value / 10000);
      const schedule = plan.payment_plan;
      const first = schedule[0];
      const purchase = Number(this.dataset.productPrice);
      const validAmount = (value) => typeof value === 'number' && Number.isInteger(value) && value >= 0;
      // Avoid extrapolating the standard monthly example to deferred/incomplete plans.
      const startDate = new Date(Number(first.due_date) * 1000);
      const monthlySchedule = schedule.every((item, index) => {
        const date = new Date(Number(item.due_date) * 1000);
        return date.getFullYear() * 12 + date.getMonth()
          === startDate.getFullYear() * 12 + startDate.getMonth() + index;
      });
      const standardPlan = monthlySchedule && !this.isDeferred(plan) && schedule.length === Number(plan.installments_count)
        && schedule.length > 1 && this.isDueToday(first.due_date)
        && schedule.every((item) => validAmount(item.purchase_amount) && validAmount(item.total_amount))
        && schedule.reduce((sum, item) => sum + item.purchase_amount, 0) === purchase;
      const example = this.querySelector('[data-alma-example]');
      if (!hasRate || !standardPlan) {
        example.textContent = this.dataset.exampleUnavailable;
        return;
      }
      const fees = Number(plan.installments_count) <= 4 && this.getTotalCost(plan) > 0
        ? this.interpolate(this.dataset.exampleFees, { fees: this.formatMoney(this.getTotalCost(plan)) }) : '';
      example.textContent = this.interpolate(this.dataset.example, {
        purchase: this.formatMoney(purchase), credit: this.formatMoney(purchase - first.purchase_amount),
        rate: formatRate(Number(plan.installments_count) > 4 ? rate : 0), months: schedule.length - 1,
        deposit: this.formatMoney(first.total_amount), fees,
        repayable: this.formatMoney(schedule.slice(1).reduce((sum, item) => sum + item.total_amount, 0))
      });
    }

    renderSchedule(plan) {
      const fragment = document.createDocumentFragment();
      const list = document.createElement('ol');
      list.className = 'alma-custom__schedule-list unstyled-list';

      plan.payment_plan.forEach((installment) => {
        const item = document.createElement('li');
        const amount = document.createElement('strong');
        const dateTag = document.createElement('time');

        item.className = 'alma-custom__schedule-item';
        amount.className = 'alma-custom__schedule-amount';
        amount.textContent = this.formatMoney(installment.total_amount);
        dateTag.className = 'alma-custom__date-tag';
        dateTag.dateTime = new Date(Number(installment.due_date) * 1000).toISOString();
        dateTag.textContent = this.isDueToday(installment.due_date)
          ? this.dataset.today
          : this.formatScheduleDate(installment.due_date);
        item.append(dateTag, amount);
        list.append(item);
      });

      fragment.append(list, this.createTotalSummary(plan));
      this.scheduleContainer.replaceChildren(fragment);
    }

    createTotalSummary(plan) {
      const summary = document.createElement('div');
      const total = document.createElement('strong');
      const totalDue = plan.payment_plan.reduce((total, installment) => total + Number(installment.total_amount || 0), 0);
      const totalCost = this.getTotalCost(plan);

      summary.className = 'alma-custom__total';
      total.className = 'alma-custom__total-value';
      total.textContent = this.interpolate(this.dataset.totalSummary, { amount: this.formatMoney(totalDue) });
      summary.append(total);

      if (Number.isFinite(totalCost)) {
        const fees = document.createElement('span');
        fees.className = 'alma-custom__total-fees';
        const rate = plan.annual_interest_rate;
        const hasRate = typeof rate === 'number' && Number.isFinite(rate) && rate >= 0;
        const apr = hasRate
          ? this.interpolate(this.dataset.apr, { rate: new Intl.NumberFormat(this.dataset.locale || 'fr-FR', {
            style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2
          }).format(rate / 10000) })
          : this.dataset.aprUnavailable;
        fees.textContent = this.interpolate(this.dataset.feesSummary, {
          amount: this.formatMoney(totalCost), apr
        });
        summary.append(fees);
      }

      return summary;
    }

    getPlanName(plan) {
      const installmentsCount = Number(plan.installments_count);

      if (installmentsCount > 1) {
        return this.interpolate(this.dataset.payIn, { count: installmentsCount });
      }

      if (Number(plan.deferred_days) > 0) {
        return this.interpolate(this.dataset.payLaterDays, { count: plan.deferred_days });
      }

      if (Number(plan.deferred_months) > 0) {
        return this.interpolate(this.dataset.payLaterMonths, { count: plan.deferred_months });
      }

      return this.dataset.payLater;
    }

    getTotalCost(plan) {
      if (
        plan.customer_total_cost_amount !== null
        && plan.customer_total_cost_amount !== undefined
        && Number.isFinite(Number(plan.customer_total_cost_amount))
      ) {
        return Number(plan.customer_total_cost_amount);
      }

      return plan.payment_plan.reduce(
        (total, installment) => total + Number(installment.customer_fee || 0) + Number(installment.customer_interest || 0),
        0
      );
    }

    isDeferred(plan) {
      return Number(plan.deferred_days) > 0 || Number(plan.deferred_months) > 0;
    }

    isDueToday(timestamp) {
      const dueDate = new Date(Number(timestamp) * 1000);
      const today = new Date();

      return dueDate.getFullYear() === today.getFullYear()
        && dueDate.getMonth() === today.getMonth()
        && dueDate.getDate() === today.getDate();
    }

    selectPlanFromEvent(event) {
      const button = event.target.closest('[data-alma-plan-index]');

      if (!button || !this.plansContainer.contains(button)) {
        return;
      }

      this.selectedPlanIndex = Number(button.dataset.almaPlanIndex);
      this.renderSelectedPlan();
    }

    onVariantChange(event) {
      if (event.detail?.formId !== this.dataset.productFormId) {
        return;
      }

      const price = Number(event.detail.variant?.price);

      if (!Number.isInteger(price) || price <= 0) {
        this.hidden = true;
        return;
      }

      this.dataset.productPrice = String(price);
      this.refreshEligibility();
    }

    open() {
      if (!this.dialog || this.hidden) {
        return;
      }

      this.previouslyFocusedElement = document.activeElement;
      this.dialog.showModal();
      this.closeButton?.focus();
    }

    close() {
      if (this.dialog?.open) {
        this.dialog.close();
        this.previouslyFocusedElement?.focus();
      }
    }

    closeFromBackdrop(event) {
      if (event.target === this.dialog) {
        this.close();
      }
    }

    formatMoney(amount) {
      return new Intl.NumberFormat(this.dataset.locale || 'fr-FR', {
        style: 'currency',
        currency: this.dataset.currencyCode || 'EUR'
      }).format(Number(amount || 0) / 100);
    }

    formatDate(timestamp) {
      return new Intl.DateTimeFormat(this.dataset.locale || 'fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }).format(new Date(Number(timestamp) * 1000));
    }

    formatScheduleDate(timestamp) {
      return new Intl.DateTimeFormat(this.dataset.locale || 'fr-FR', {
        day: 'numeric',
        month: 'short'
      }).format(new Date(Number(timestamp) * 1000));
    }

    interpolate(template = '', values = {}) {
      return Object.entries(values).reduce(
        (result, [key, value]) => result.split(`__${key}__`).join(String(value)),
        template
      );
    }
  }

  if (!window.customElements.get('alma-custom-widget')) {
    window.customElements.define('alma-custom-widget', AlmaCustomWidget);
  }
})();
